export const VIDEO_LIMITS = {
  minBytes: 75 * 1024,
  maxBytes: 500 * 1024 * 1024,
  minSeconds: 3,
  maxSeconds: 30 * 60,
  posterMaxBytes: 500 * 1024,
};

const POSTER_MAX_WIDTH = 1280;
const SEEK_TIMEOUT_MS = 8000;

const loadVideo = (url, preload) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = preload;
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => resolve(video);
    video.onerror = () => reject(new Error("unreadable"));
    video.src = url;
  });

// Returns { ok: true, duration } or { ok: false, reason }.
// reason: "type" | "tooSmall" | "tooLarge" | "unreadable" | "tooShort" | "tooLong"
export const validateVideoFile = async (file) => {
  const isMp4 = file.type === "video/mp4" || /\.mp4$/i.test(file.name);
  if (!isMp4) return { ok: false, reason: "type" };
  if (file.size < VIDEO_LIMITS.minBytes) return { ok: false, reason: "tooSmall" };
  if (file.size > VIDEO_LIMITS.maxBytes) return { ok: false, reason: "tooLarge" };

  const url = URL.createObjectURL(file);
  try {
    const video = await loadVideo(url, "metadata");
    const duration = video.duration;
    if (!Number.isFinite(duration)) return { ok: false, reason: "unreadable" };
    if (duration < VIDEO_LIMITS.minSeconds) return { ok: false, reason: "tooShort" };
    if (duration > VIDEO_LIMITS.maxSeconds) return { ok: false, reason: "tooLong" };
    return { ok: true, duration };
  } catch {
    return { ok: false, reason: "unreadable" };
  } finally {
    URL.revokeObjectURL(url);
  }
};

const seekTo = (video, time) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("seek timeout")), SEEK_TIMEOUT_MS);
    video.onseeked = () => {
      clearTimeout(timer);
      resolve();
    };
    video.currentTime = time;
  });

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));

// Draws the current frame and lowers quality, then size, until it is <= 500 KB.
const frameToPoster = async (video) => {
  const baseScale = Math.min(1, POSTER_MAX_WIDTH / video.videoWidth);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  for (const factor of [1, 0.75, 0.5, 0.35]) {
    const scale = baseScale * factor;
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    for (let quality = 0.85; quality >= 0.35; quality -= 0.1) {
      const blob = await canvasToBlob(canvas, quality);
      if (blob && blob.size <= VIDEO_LIMITS.posterMaxBytes) return blob;
    }
  }
  return null;
};

// Evenly spaced candidate posters, the first one at about 1 s.
// Returns [{ time, blob }]. The caller creates preview URLs and revokes them.
export const captureFrames = async (file, duration, count = 5) => {
  const url = URL.createObjectURL(file);
  try {
    const video = await loadVideo(url, "auto");
    const start = 1;
    const end = Math.max(start, duration - 1);
    const step = count > 1 ? (end - start) / (count - 1) : 0;

    const frames = [];
    for (let i = 0; i < count; i++) {
      const time = start + step * i;
      try {
        await seekTo(video, time);
        const blob = await frameToPoster(video);
        if (blob) frames.push({ time, blob });
      } catch {
        /* skip a frame that cannot be read */
      }
    }
    return frames;
  } finally {
    URL.revokeObjectURL(url);
  }
};